import {Button} from "primereact/button";
import React, { useEffect, useState } from 'react';
import ChartTypeMenu from "./ChartTypeMenu";
import DateTimeRangeSelection from "./DateTimeRangeSelection";
import DragArea from "./DragArea/DragArea";
import SettingsDialog from "./SettingsDialog";
import {AggregationType, ChartType, Dataset} from "./models/types";
import {isDate2BeforeDate1} from "./utils/utils";
import {useResizeDetector} from "react-resize-detector";
import MultiDragArea from "./DragArea/MultiDragArea";
import DatasetInfoByID from "../dataset-info-by-id";

import ChartRenderer from "./ChartRenderer";
import { addToDate } from '../../../utils/time-utils';
import DatasetsPropertiesTable from "../../basic/datasets-properties/DatasetsPropertiesTable";
import {Dialog} from "primereact/dialog";
import {useGetDataInfoRange} from "api/api-hooks/datasets-hooks";

const DatasetView = ({selectedDatasetsID, setSelectedDatasets, removeDataset}: {
    selectedDatasetsID: Array<Dataset>,
    setSelectedDatasets: (arr: Array<Dataset>) => void,
    removeDataset: any
}) => {
    const [selectedChartType, setSelectedChartType] = useState<ChartType>("LINE");

    const [dateFrom, setDateFrom] = useState<Date>(addToDate(new Date(), -6, "HOURS"));
    const [dateTo, setDateTo] = useState<Date>(new Date());

    const [invalidRange, setInvalidRange] = useState<boolean>(false);

    const [dataPointCount, setDataPointCount] = useState<number>(0);

    const [displayProperties, setDisplayProperties] = useState<boolean>(false);
    const [displaySettings, setDisplaySettings] = useState<boolean>(false);

    const [enableSettings, setEnableSettings] = useState<boolean>(false);

    const [showAxisSelection, setShowAxisSelection] = useState<boolean>(false);
    const [enableMultiAxis, setEnableMultiAxkis] = useState<boolean>(false);
    const [selectedColorPalette, setSelectedColorPalette] = useState<{ key: string; value: Array<string> }>({
        key: "Category10",
        value: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"],
    });

    const [xAxisID, setXAxisID] = useState<string>("");
    const [yAxisIDS, setYAxisIDS] = useState<Array<string>>([]);

    const [x1AxisID, setX1AxisID] = useState<string>("");
    const [y1AxisID, setY1AxisID] = useState<string>("");
    const [colorAxisID, setColorAxisID] = useState<string>("");
    const [sizeAxisID, setSizeAxisID] = useState<string>("");

    const [colorScale, setColorScale] = useState<boolean>(true);

    const [aggregationMethod, setAggregationMethod] = useState<AggregationType>("SUM");

    const {
        data,
        isLoading,
        isError,
        refetch,
        isRefetching
    } = useGetDataInfoRange(selectedDatasetsID.map(e => e.identifier),
        {
            timeStart: dateFrom.toISOString(),
            timeEnd: dateTo.toISOString(),
            usePromiseHandler: (res) => {
                return res.data
            }
        }, {
            enabled: (selectedDatasetsID.length > 0) && dateFrom.getTime() < dateTo.getTime(),
            refetchOnWindowFocus: false
        })

    useEffect(() => {
        if (data != null) {
            setDataPointCount(data.reduce((acc: number, current: any) => acc + current.data.length, 0))
        } else {
            setDataPointCount(0)
        }
    }, [data]);

    useEffect(() => {
        if (isDate2BeforeDate1(dateFrom, dateTo)) {
            setInvalidRange(true);
        } else {
            setDataPointCount(0);
            setInvalidRange(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        if (selectedDatasetsID.length === 0) {
            setEnableSettings(false);
        }
    }, [selectedDatasetsID]);


    useEffect(() => {
        if (selectedChartType.length > 0 && selectChartType != null && !isLoading) {
            setEnableSettings(true);
        }
    }, [selectedChartType]);

    const selectChartType = (chartType: ChartType) => {
        setSelectedChartType(chartType);
        if (chartType === "SCATTER") {
            setShowAxisSelection(true);
        } else {
            setShowAxisSelection(false);
        }

        if (chartType === "MULTISCATTER") {
            setEnableMultiAxkis(true);
        } else {
            setEnableMultiAxkis(false);
        }
    };

    const selectAggregationMethod = (aggregationMethod: AggregationType) => {
        setAggregationMethod(aggregationMethod);
    };

    const handleSettings = () => {
        setDisplaySettings(true);
    };

    const onHide = () => {
        setDisplaySettings(false);
    };

    const selectColorPalette = (colorsArr: { key: string; value: Array<any> }) => {
        setSelectedColorPalette(colorsArr);
    };

    const assignToAxis = (xAxisId: string, yAxisIds: Array<string>) => {
        setXAxisID(xAxisId);
        setYAxisIDS(yAxisIds);
    };

    const assignToMultiAxis = (x1AxisId: string, y1AxisId: string, colorAxisId: string, sizeAxisId: string, scale: boolean) => {
        setX1AxisID(x1AxisId);
        setY1AxisID(y1AxisId);
        setColorAxisID(colorAxisId);
        setSizeAxisID(sizeAxisId);
        setColorScale(scale)
    };

    const {width, ref: refOptions} = useResizeDetector();
    const {height, ref,width:widthWindow} = useResizeDetector();

    return <div className="datasetView dynamicRow fullWidth">
        {displaySettings && selectedChartType && (
            <SettingsDialog
                displayDialog={displaySettings}
                selectedChartType={selectedChartType}
                onHide={onHide}
                data={data}
                selectColorPalette={selectColorPalette}
                selectedColorPalette={selectedColorPalette}
                selectAggregationMethod={selectAggregationMethod}
                aggregationMethod={aggregationMethod}
            />
        )}

        {displayProperties &&
            <Dialog onHide={() => setDisplayProperties(false)}
                    visible={displayProperties}
                    header={"Datasets Properties"}
                    modal={false}
            >
                <DatasetsPropertiesTable identifiers={selectedDatasetsID.map(e => e.identifier)}/>
            </Dialog>
        }

        <div className="chartMenu cardSlim">
            <ChartTypeMenu selectedChartType={selectedChartType}
                           setSelectedCharType={selectChartType}
                           selectedDatasetID={selectedDatasetsID}
                           setSelectedDatasets={setSelectedDatasets}
            />
        </div>


        <div className="flexColumn fullWidth">
            <div className="optionsFields cardSlim" ref={refOptions}>
                <div className="dateTimeSelection center">
                    <DateTimeRangeSelection
                        selectDateFrom={setDateFrom}
                        selectDateTo={setDateTo}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        invalidRange={invalidRange}
                    />
                </div>
                <div className="side flexRow center">
                    {!isLoading && (
                        <div className="flexColumn center">
                            <h3>{selectedChartType === "RAW" ? "DATA TABLE" : selectedChartType}</h3>
                            <p>Loaded data points: {dataPointCount}</p>
                        </div>
                    )}
                    <DatasetInfoByID/>
                    <Button
                        icon="pi pi-info-circle"
                        onClick={() => setDisplayProperties(true)}
                        className="p-button-rounded p-button-secondary p-button-text center"
                        disabled={selectedDatasetsID.length === 0}
                    ></Button>
                    <Button
                        icon="pi pi-cog"
                        iconPos="left"
                        onClick={handleSettings}
                        className="p-button-rounded p-button-secondary p-button-text center"
                        disabled={!enableSettings || !data}
                    ></Button>
                    <Button
                        icon="pi pi-refresh"
                        iconPos="left"
                        onClick={() => refetch()}
                        className="p-button-rounded p-button-secondary p-button-text center"
                        disabled={isLoading || isError || isRefetching || !data}
                        loading={isLoading || isRefetching}
                    ></Button>
                </div>
            </div>

            <div className="dataView" style={{width: width}} ref={ref}>
                <ChartRenderer selectedChartType={selectedChartType}
                               data={selectedDatasetsID.length > 0 ? data : []}
                               isLoading={isLoading}
                               isError={isError}
                               colorPallet={selectedColorPalette}
                               dataPointCount={dataPointCount}
                               singleAxis={{x: xAxisID, y: yAxisIDS}}
                               multiAxis={{x: x1AxisID, y: y1AxisID, color: colorAxisID, size: sizeAxisID}}
                               colorScale={colorScale}
                               selectedDatasetsID={selectedDatasetsID}
                               aggregationType={aggregationMethod}
                               windowWidth={widthWindow ? widthWindow : 500}
                               windowHeight={height ? height < 500 ? height : 500 : 500}
                />
            </div>

            <div className="cardSlim">
                {!enableMultiAxis &&
                    <DragArea selectedDatasetsArr={selectedDatasetsID}
                              removeDataset={removeDataset}
                              showAxisSelection={showAxisSelection}
                              assignToAxis={assignToAxis}
                    />
                }

                {enableMultiAxis &&
                    <MultiDragArea selectedDatasetsArr={selectedDatasetsID}
                                   removeDataset={removeDataset}
                                   enableXAxis={true}
                                   assignToMultiAxis={assignToMultiAxis}
                                   colorScale={colorScale}
                                   setColorScale={setColorScale}
                    />
                }
            </div>
        </div>
    </div>
};

export default DatasetView;
